import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import OnlineStorePage from './pages/OnlineStorePage';
import ContactPage from './pages/ContactPage';
import StaffContactPage from './pages/StaffContactPage';
import Safety from './pages/Safety';
import CalendarPage from './pages/CalendarPage';
import LearnMorePage from './pages/LearnMorePage';
import NewsDetailPage from './pages/NewsDetailPage';
import ProgramDetailPage from './pages/ProgramDetailPage';
import SplashScreen from './components/SplashScreen';
import PreK from './pages/Classes/PreK';
import Kindergarten from './pages/Classes/Kindergarten';
import FirstGrade from './pages/Classes/FirstGrade';
import SecondGrade from './pages/Classes/SecondGrade';
import ThirdGrade from './pages/Classes/ThirdGrade';
import FourthGrade from './pages/Classes/FourthGrade'
import FifthGrade from './pages/Classes/FifthGrade';
import Science from './pages/Classes/Science';
import LibraryTech from './pages/Classes/LibraryTech';
import Music from './pages/Classes/Music';
import NIA from './pages/Classes/NIA';
import PhysicalEducation from './pages/Classes/PhysicalEducation';
import SocialStudies from './pages/Classes/SocialStudies';
import AfterschoolNIA from './pages/ParentResources/AfterschoolNIA.js';
import BusInfo from './pages/ParentResources/BusInfo.js';
import CellphonePolicy from './pages/ParentResources/CellphonePolicy.js';
import DismissalLocations from './pages/ParentResources/DismissalLocations.js';
import ESL from './pages/ParentResources/ESL.js';
import IEP from './pages/ParentResources/IEP.js';
import ParentHandbook from './pages/ParentResources/ParentHandbook.js';
import ParentCoordinatorCorner from './pages/ParentCoordinatorCorner.js';

// --- Stripe Imports ---
// import { loadStripe } from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js';
// import { Elements } from '@stripe/react-stripe-js';
import { Elements } from '@stripe/react-stripe-js';

// --- Load Stripe ---
// Make sure to call loadStripe outside of a component's render.
// Load your publishable key from an environment variable.
const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;
if (!stripePublishableKey) {
  console.error("Stripe Publishable Key is not set. Please set REACT_APP_STRIPE_PUBLISHABLE_KEY.");
  // Potentially handle this more gracefully, e.g., by not initializing Stripe or showing an error
}
const stripePromise = stripePublishableKey ? loadStripe(stripePublishableKey) : null;

// ScrollToTop component to reset scroll position on page change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Component to conditionally apply padding based on route
const MainContent = ({ children }) => {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const [topOffset, setTopOffset] = useState(0);

  // Calculate header heights for proper content positioning
  useEffect(() => {
    const updateOffset = () => {
      const navbar = document.querySelector('nav');
      let offset = 0;
      
      if (navbar) {
        offset += navbar.offsetHeight;
      }
      
      setTopOffset(offset);
    };
    
    updateOffset();
    window.addEventListener('resize', updateOffset);
    
    return () => {
      window.removeEventListener('resize', updateOffset);
    };
  }, [isHomePage]);
  
  return (
    <main 
      className={`flex-1 w-full ${isHomePage ? '' : 'px-4 max-w-7xl mx-auto'}`}
      style={!isHomePage ? { paddingTop: `${topOffset + 20}px` } : {}}
    >
      {children}
    </main>
  );
};

function App() {
  const [showSplash, setShowSplash] = useState(true);
  const [fadeOutSplash, setFadeOutSplash] = useState(false);
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    const fadeTimer = setTimeout(() => {
      setFadeOutSplash(true);
      setShowContent(true);
    }, 1500);

    const removeSplashTimer = setTimeout(() => {
      setShowSplash(false);
    }, 2500);

    return () => {
      clearTimeout(fadeTimer);
      clearTimeout(removeSplashTimer);
    };
  }, []);

  return (
    <Router>
      {showSplash && <SplashScreen fadeOut={fadeOutSplash} />}

      <div 
        className={`flex flex-col min-h-screen transition-opacity duration-1000 ease-in-out ${
          showContent ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ pointerEvents: showContent ? 'auto' : 'none' }}
      >
        <ScrollToTop />
        <Navbar />
        <MainContent>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            {/* Ensure this is the active route for /store */}
            <Route 
              path="/store" 
              element={
                <Elements stripe={stripePromise}>
                  <OnlineStorePage />
                </Elements>
              } 
            />
            {/* Remove or ensure any other /store routes are commented out */}
            {/* e.g., <Route path="/store" element={<OnlineStorePage />} /> */}
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/contact-us" element={<StaffContactPage />} />
            <Route path="/safety" element={<Safety />} />
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/learn-more" element={<LearnMorePage />} />
            <Route path="/news/:slug" element={<NewsDetailPage />} />
            <Route path="/programs/:slug" element={<ProgramDetailPage />} />
            <Route path="/classes/pre-k" element={<PreK />} />
            <Route path="/classes/kindergarten" element={<Kindergarten />} />
            <Route path="/classes/first-grade" element={<FirstGrade />} />
            <Route path="/classes/second-grade" element={<SecondGrade />} />
            <Route path="/classes/third-grade" element={<ThirdGrade />} />
            <Route path="/classes/fourth-grade" element={<FourthGrade />} />
            <Route path="/classes/fifth-grade" element={<FifthGrade />} />
            <Route path="/classes/science" element={<Science />} />
            <Route path="/classes/library-tech" element={<LibraryTech />} />
            <Route path="/classes/music" element={<Music />} />
            <Route path="/classes/nia" element={<NIA />} />
            <Route path="/classes/physical-education" element={<PhysicalEducation />} />
            <Route path="/classes/social-studies" element={<SocialStudies />} />
            <Route path="/parent-resources/afterschool-nia" element={<AfterschoolNIA />} />
            <Route path="/parent-resources/bus-info" element={<BusInfo />} />
            <Route path="/parent-resources/cellphone-policy" element={<CellphonePolicy />} />
            <Route path="/parent-resources/dismissal-locations" element={<DismissalLocations />} />
            <Route path="/parent-resources/esl" element={<ESL />} />
            <Route path="/parent-resources/iep" element={<IEP />} />
            <Route path="/parent-resources/parent-handbook" element={<ParentHandbook />} />
            <Route path="/parent-coordinator-corner" element={<ParentCoordinatorCorner />} />
          </Routes>
        </MainContent>
        <Footer />
      </div>
    </Router>
  );
}

export default App;
